import React, { useState, useEffect } from 'react';
import { X, Search, Plus, Check } from 'lucide-react';
import { Service, ServiceCategory, getServices, getServiceCategories } from '../../lib/services';
import { useBusiness } from '../../contexts/BusinessContext';
import CreateServiceModal from '../CreateServiceModal';
import { supabase } from '../../lib/supabase';
import { useEventDrivenModalPersistence } from '../../utils/modalPersistence';

interface SelectServiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (service: Service, priceOption: any) => Promise<void>;
  bookingId?: string;
  vehicleId?: string;
}

export default function SelectServiceModal({ 
  isOpen, 
  onClose,
  onAdd,
  bookingId,
  vehicleId,
}: SelectServiceModalProps) {
  const { selectedBusiness } = useBusiness();
  const [isAnimating, setIsAnimating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [services, setServices] = useState<Service[]>([]);
  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPriceOptions, setSelectedPriceOptions] = useState<Record<string, string>>({});
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedServices, setSelectedServices] = useState<Set<string>>(new Set());

  // Event-driven modal persistence for CreateServiceModal
  const { hasModalData } = useEventDrivenModalPersistence('create_service');

  const loadData = async () => {
    if (!selectedBusiness?.id) return;
    
    try {
      setLoading(true);
      const [servicesData, categoriesData] = await Promise.all([
        getServices(selectedBusiness.id),
        getServiceCategories(selectedBusiness.id)
      ]);
      
      setServices(servicesData);
      setCategories(categoriesData);

      // Initialize default price options
      const defaultOptions: Record<string, string> = {};
      servicesData.forEach(service => {
        if (service.price_options?.length > 0) {
          defaultOptions[service.id] = service.price_options[0].id;
        }
      });
      setSelectedPriceOptions(defaultOptions);
      
      setError(null);
    } catch (err) {
      console.error('Error loading services:', err);
      setError(err instanceof Error ? err.message : 'Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      loadData();
    }
  }, [isOpen, selectedBusiness?.id]);

  // Check for saved modal data on component mount and auto-open if found
  useEffect(() => {
    const checkSavedModalData = () => {
      const hasData = hasModalData();
      console.log('🔍 Checking for saved CreateServiceModal data in SelectServiceModal:', hasData);
      if (hasData) {
        console.log('📥 Found saved CreateServiceModal progress - reopening modal (SelectServiceModal.tsx)');
        setIsCreateModalOpen(true);
      }
    };
    
    // Check immediately first
    checkSavedModalData();
    
    // Also check with multiple delays to handle different initialization timings
    const timer1 = setTimeout(checkSavedModalData, 100);
    const timer2 = setTimeout(checkSavedModalData, 500);
    const timer3 = setTimeout(checkSavedModalData, 1000);
    
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, [hasModalData]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      setSearchQuery('');
      setSelectedServices(new Set());
    }
  };

  const handleServiceSelect = async (service: Service, priceOption: any) => {
    try {
      await onAdd(service, priceOption);
      onClose();
    } catch (err) {
      console.error('Error selecting service:', err);
      setError(err instanceof Error ? err.message : 'Failed to select service');
    }
  };

  // Group services by category
  const servicesByCategory = categories.map(category => ({
    ...category,
    services: services.filter(service => service.category_id === category.id)
  })).filter(category => category.services.length > 0);

  // Filter categories and services based on search
  const filteredCategories = servicesByCategory.filter(category => {
    if (!searchQuery) return true;
    
    const searchLower = searchQuery.toLowerCase();
    return (
      category.name.toLowerCase().includes(searchLower) ||
      category.services.some(service => 
        service.name.toLowerCase().includes(searchLower) ||
        service.description?.toLowerCase().includes(searchLower)
      )
    );
  });

  if (!isOpen && !isAnimating) return null;

  return (
    <div 
      className={`fixed inset-0 bg-black transition-opacity duration-300 ${
        isOpen ? 'bg-opacity-50' : 'bg-opacity-0'
      } z-[100000]`}
      onClick={onClose}
      style={{
        // Ensure modal appears above BookingInfoModal on mobile
        zIndex: 100000,
        transform: 'translateZ(0)',
        WebkitTransform: 'translateZ(0)',
        isolation: 'isolate'
      }}
    >
      <div 
        className={`fixed inset-y-0 right-0 w-full md:w-[600px] bg-white transform transition-transform duration-300 ease-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        onTransitionEnd={handleTransitionEnd}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col">
          <div className="px-6 py-4 border-b border-[#2f2f2f] bg-[#1a1a1a] flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white">Select Service</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-300">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="p-4 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search services..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
                  <p className="mt-4 text-sm text-gray-600">Loading services...</p>
                </div>
              </div>
            ) : error ? (
              <div className="text-center py-6">
                <div className="text-red-600 mb-2">{error}</div>
                <button
                  onClick={loadData}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  Try again
                </button>
              </div>
            ) : filteredCategories.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-sm font-medium text-gray-900">
                  {searchQuery ? 'No matching services' : 'No services available'}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  {searchQuery ? 'Try adjusting your search terms' : 'Add services to get started'}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredCategories.map((category) => (
                  <div key={category.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                    <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                      <h3 className="text-sm font-medium text-gray-900">{category.name}</h3>
                    </div>
                    <div className="divide-y divide-gray-200">
                      {category.services.map((service) => (
                        <div key={service.id} className="p-4">
                          <div className="flex-1">
                            <div className="text-sm font-medium text-gray-900">{service.name}</div>
                            <p className="mt-1 text-sm text-gray-500">{service.description}</p>
                            
                            <div className="mt-3 flex flex-wrap gap-2">
                              {service.price_options?.map((option) => {
                                const key = `${service.id}-${option.id}`;
                                const isSelected = selectedServices.has(key);
                                
                                return (
                                  <button
                                    key={option.id}
                                    onClick={() => {
                                      handleServiceSelect(service, option);
                                      setSelectedServices(prev => {
                                        const next = new Set(prev);
                                        next.add(key);
                                        return next;
                                      });
                                    }}
                                    disabled={loading}
                                    className={`inline-flex items-center px-3 py-1.5 text-xs font-medium text-white rounded-md transition-colors ${
                                      isSelected ? 'bg-green-600 hover:bg-green-700' : 'bg-green-500 hover:bg-green-600'
                                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                                  >
                                    {selectedServices.has(key) ? (
                                      <>
                                        <Check className="h-3.5 w-3.5 mr-1.5" />
                                        Added
                                      </>
                                    ) : (
                                      <>
                                        <Plus className="h-3.5 w-3.5 mr-1.5" />
                                        {option.name} - ${option.price.toLocaleString()}
                                      </>
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-gray-900 rounded-md hover:bg-gray-800"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Service
          </button>
        </div>
      </div>

      <CreateServiceModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={async () => {
          setIsCreateModalOpen(false);
          await loadData();
        }}
        businessId={selectedBusiness?.id}
      />
    </div>
  );
}